import { Waves, Sunrise, Feather, Users } from "lucide-react";
import { FadeUp, SectionHeading } from "../components/motion";

const PILLARS = [
  {
    tag: "01",
    word: "REST",
    icon: Waves,
    body: "Unhurried time to enjoy the ocean, the ship, destinations, and personal quiet.",
    img: "https://images.unsplash.com/photo-1688269910608-e3c65eb4ac3a?q=80&w=1600&auto=format&fit=crop",
    alt: "Clean deck lines of a modern ocean vessel sailing through calm seas",
    span: "lg:col-span-7",
  },
  {
    tag: "02",
    word: "RENEW",
    icon: Sunrise,
    body: "Optional spiritual reflection, prayer, journaling, and conversations about calling, boundaries, and caring for yourself.",
    img: "https://images.unsplash.com/photo-1474540412665-1cdae210ae6b?q=80&w=1600&auto=format&fit=crop",
    alt: "Twilight hues settling over calm ocean water",
    span: "lg:col-span-5",
  },
  {
    tag: "03",
    word: "WRITE",
    icon: Feather,
    body: "Introductory workshops on finding your message, shaping an outline, and understanding the steps toward writing and publishing a book. No writing experience or publishing goal is required.",
    img: "https://images.unsplash.com/photo-1473186505569-9c61870c11f9?q=80&w=1600&auto=format&fit=crop",
    alt: "A fountain pen resting on a handwritten journal",
    span: "lg:col-span-5",
  },
  {
    tag: "04",
    word: "CONNECT",
    icon: Users,
    body: "Fellowship with other chaplains and optional opportunities to meet participating organizations. Optional introductory conversations about chaplaincy roles, service settings, and educational pathways are planned, subject to the final retreat program.",
    img: "https://images.unsplash.com/photo-1676407118683-f0060c12b94f?q=80&w=1600&auto=format&fit=crop",
    alt: "The deck of a cruise ship at sunset",
    span: "lg:col-span-7",
  },
];

export default function Experience() {
  return (
    <section
      id="experience"
      data-testid="section-experience"
      className="grain relative overflow-hidden bg-navy py-24 text-cream sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <SectionHeading eyebrow="The Experience" titleLines={["Four movements", "of the retreat."]} />
          <FadeUp delay={0.2}>
            <p className="max-w-sm border-l-2 border-gold/60 pl-5 text-sm leading-relaxed text-cream/75" data-testid="experience-gentle-note">
              A gentle schedule: sessions are optional, and free time is substantial.
            </p>
          </FadeUp>
        </div>

        <div className="mt-14 grid gap-5 lg:grid-cols-12" data-testid="experience-grid">
          {PILLARS.map((p, i) => (
            <FadeUp key={p.word} delay={0.08 * i} className={p.span + " h-full"}>
              <article
                data-testid={"experience-card-" + p.word.toLowerCase()}
                className="group flex h-full flex-col overflow-hidden border border-gold/25 bg-navy-abyss/60 transition-colors duration-500 hover:border-gold/60"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={p.img}
                    alt={p.alt}
                    loading="lazy"
                    className="h-52 w-full object-cover opacity-90 transition-all duration-[1.8s] ease-out group-hover:scale-105 group-hover:opacity-100 sm:h-60"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-navy-abyss/80 via-transparent to-transparent" />
                  <p.icon
                    size={22}
                    strokeWidth={1.25}
                    className="absolute right-5 top-5 text-gold-light"
                    aria-hidden="true"
                  />
                </div>
                <div className="flex flex-1 flex-col p-7 sm:p-8">
                  <p className="font-mono text-[11px] tracking-[0.3em] text-gold-light">{p.tag}</p>
                  <h3 className="mt-3 font-serif text-3xl tracking-wide text-cream sm:text-4xl">{p.word}</h3>
                  <p className="mt-4 text-sm font-light leading-relaxed text-cream/75">{p.body}</p>
                </div>
              </article>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.1}>
          <div className="mt-12 grid gap-8 border border-gold/25 bg-navy-abyss/50 p-8 sm:grid-cols-2 sm:p-10" data-testid="experience-formats">
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold-light">Formats in exploration</p>
              <p className="mt-4 text-sm leading-relaxed text-cream/75">
                We are exploring shorter and longer cruise formats, including approximately three-
                to five-day retreats. Final itineraries and onboard meeting-space arrangements will
                be announced when confirmed.
              </p>
            </div>
            <div>
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-gold-light">What to expect</p>
              <p className="mt-4 text-sm leading-relaxed text-cream/75">
                A gentle schedule with optional sessions and substantial free time. No writing
                experience or publishing goal is required, and a completed book is not promised
                during the retreat.
              </p>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}
